import { useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle, X, XCircle } from 'lucide-react';
import * as Sentry from '@sentry/react';
import { Modal } from '@/components/modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { TagInput } from '@/components/ui/tag-input';
import { DialogClose } from '@/components/ui/dialog';
import { LoadingSpinner } from '@/components/loading-spinner';
import { useNotification } from '@/components/notifications';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useCreateOrgMcpTool,
  useUpdateOrgMcpTool,
  useValidateOrgMcpTool,
} from '@/hooks/mutations/org-mcp-tools/use-org-mcp-tool-mutations';
import {
  DiscoveredTool,
  OrgMcpTool,
  OrgMcpToolAuthType,
  OrgMcpToolConnectionInput,
  OrgMcpToolTransport,
} from '@/services/org-mcp-tools/types';

interface AddEditToolModalProps {
  orgId: string;
  /** Editing an existing tool; omit for create. */
  tool?: OrgMcpTool | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

const AUTH_TYPES: { value: OrgMcpToolAuthType; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'api_key', label: 'API key (header)' },
  { value: 'bearer', label: 'Bearer token' },
  { value: 'custom_headers', label: 'Custom headers (JSON)' },
];

const TRANSPORTS: { value: OrgMcpToolTransport; label: string }[] = [
  { value: 'streamable_http', label: 'Streamable HTTP' },
  { value: 'sse', label: 'SSE' },
];

export const AddEditToolModal = ({
  orgId,
  tool,
  isOpen,
  onOpenChange,
}: AddEditToolModalProps) => {
  const notification = useNotification();
  const isEditing = !!tool;

  const validate = useValidateOrgMcpTool(orgId);
  const create = useCreateOrgMcpTool(orgId);
  const update = useUpdateOrgMcpTool(orgId);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [iconUrl, setIconUrl] = useState('');
  const [serverUrl, setServerUrl] = useState('');
  const [transport, setTransport] =
    useState<OrgMcpToolTransport>('streamable_http');
  const [authType, setAuthType] = useState<OrgMcpToolAuthType>('none');
  const [authHeaderName, setAuthHeaderName] = useState('');
  const [secret, setSecret] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  // The tool universe discovered by a successful validate (null until tested).
  const [discoveredTools, setDiscoveredTools] = useState<
    DiscoveredTool[] | null
  >(null);
  // Exposed-tool selection. null (or empty) means "expose ALL tools", matching
  // the backend `selected_tools` semantics; a non-empty array is an explicit
  // subset of tool names.
  const [selectedTools, setSelectedTools] = useState<string[] | null>(null);

  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
    tools?: DiscoveredTool[];
  } | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Monotonic id for the in-flight Test Connection. Bumped on every test AND on
  // any connection-field change, so a slow validate response that resolves
  // after the user edited a field (or re-tested) is discarded instead of
  // restoring a stale tool list.
  const testReqId = useRef(0);

  // Hydrate from the tool being edited (the secret is never returned, so it
  // stays blank — leaving it blank on save keeps the stored credential).
  useEffect(() => {
    if (isOpen) {
      setName(tool?.name ?? '');
      setDescription(tool?.description ?? '');
      setIconUrl(tool?.icon_url ?? '');
      setServerUrl(tool?.server_url ?? '');
      setTransport(tool?.transport ?? 'streamable_http');
      setAuthType(tool?.auth_type ?? 'none');
      setAuthHeaderName(tool?.auth_header_name ?? '');
      setSecret('');
      setTags(tool?.tags ?? []);
      // Empty/null selected_tools means "expose all" — normalize both to null.
      setSelectedTools(
        tool?.selected_tools?.length ? tool.selected_tools : null,
      );
      setDiscoveredTools(null);
      setTestResult(null);
      setSaveError(null);
    }
  }, [isOpen, tool]);

  // Any change to a connection-defining field invalidates a prior discovery:
  // the discovered tool list + test banner belong to the previously-tested
  // endpoint/credentials, so keeping them would let the user select or save
  // tools that no longer match a working connection. Selection is left intact
  // (an edited tool's stored selection is still valid until re-tested).
  useEffect(() => {
    // Invalidate any in-flight test so its response can't restore this
    // now-stale discovery.
    testReqId.current += 1;
    setDiscoveredTools(null);
    setTestResult(null);
    // An empty selection ([]) is only reachable via the checklist; once the
    // checklist is hidden (discovery cleared) it can't be edited, so it would
    // trap Save (selectionValid=false) with no recovery but a re-test. Reset it
    // to null ("expose all") so the modal stays usable. A non-empty subset is
    // left intact (an edited tool's stored selection stays valid until re-test).
    setSelectedTools((prev) => (prev && prev.length === 0 ? null : prev));
  }, [serverUrl, transport, authType, authHeaderName, secret]);

  const needsSecret = authType !== 'none';
  // A blank secret is only acceptable when editing a tool that already has a
  // stored credential for the *same* auth type — then blank means "keep the
  // existing secret". Changing the auth type (e.g. none -> bearer), or editing
  // a tool that never had a secret, must supply a non-empty one. Create always
  // requires one for auth'd tools.
  const keepsExistingSecret =
    isEditing && !!tool?.has_secret && authType === tool?.auth_type;
  const secretSatisfied =
    !needsSecret || !!secret.trim() || keepsExistingSecret;
  const baseValid = name.trim() !== '' && serverUrl.trim() !== '';
  // Exposing zero tools is not a representable state: the backend reads
  // `selected_tools or None`, so an empty allowlist collapses to "expose all"
  // (and a server that exposes nothing is equivalent to not having it). So the
  // only valid selections are "expose all" (null) or a non-empty subset —
  // unchecking every tool is blocked at save rather than silently saved as
  // expose-all.
  const selectionValid = selectedTools === null || selectedTools.length > 0;

  const buildInput = (): OrgMcpToolConnectionInput => {
    const input: OrgMcpToolConnectionInput = {
      name: name.trim(),
      description: description.trim() || null,
      icon_url: iconUrl.trim() || null,
      source: 'direct',
      server_url: serverUrl.trim(),
      transport,
      auth_type: authType,
      tags: tags.length ? tags : null,
      // Empty selection means "expose all"; send null in that case so the
      // backend applies its all-tools default instead of an empty allowlist.
      selected_tools: selectedTools?.length ? selectedTools : null,
    };
    if (authType === 'api_key') {
      input.auth_header_name = authHeaderName.trim() || null;
    }
    if (needsSecret && secret.trim()) {
      input.secret = secret.trim();
    }
    return input;
  };

  // A tool is exposed when the whole server is (selectedTools === null) or when
  // its name is in the explicit subset.
  const toolChecked = (name: string) =>
    selectedTools === null || selectedTools.includes(name);

  const handleToggleTool = (name: string, checked: boolean) => {
    const universe = (discoveredTools ?? []).map((t) => t.name);
    const base = (selectedTools === null ? universe : selectedTools).filter(
      (n) => universe.includes(n),
    );
    const next = checked
      ? Array.from(new Set([...base, name]))
      : base.filter((n) => n !== name);
    // Collapse "every tool checked" back to null so save sends the all-tools
    // sentinel rather than the full list.
    setSelectedTools(next.length === universe.length ? null : next);
  };

  const handleToggleAll = (checked: boolean) => {
    setSelectedTools(checked ? null : []);
  };

  const allToolsState: boolean | 'indeterminate' =
    selectedTools === null
      ? true
      : selectedTools.length === 0
        ? false
        : 'indeterminate';

  const handleTest = async () => {
    if (!baseValid) {
      notification.error('Tool name and server URL are required');
      return;
    }
    // Validate always needs a usable credential for auth'd tools — the stored
    // one can't be re-read here, so require entering it to test.
    if (needsSecret && !secret.trim()) {
      notification.error('Enter the secret to test an authenticated tool');
      return;
    }
    setTestResult(null);
    const reqId = (testReqId.current += 1);
    try {
      const result = await validate.mutateAsync(buildInput());
      // A connection field changed (or a newer test started) while this request
      // was in flight — its result is stale; drop it.
      if (testReqId.current !== reqId) return;
      setTestResult({
        success: result.ok,
        message: result.ok
          ? `Connected — ${result.tools.length} tool(s) found`
          : 'Connection failed',
        tools: result.tools,
      });
      if (result.ok) {
        const tools = result.tools ?? [];
        setDiscoveredTools(tools);
        setSelectedTools((prev) => {
          if (prev === null) return null;
          // Drop previously-selected names the server no longer exposes, then
          // collapse to null when every discovered tool is selected so the
          // master toggle reads "all" and save sends the all-tools sentinel.
          const filtered = prev.filter((n) => tools.some((t) => t.name === n));
          return filtered.length === tools.length ? null : filtered;
        });
      } else {
        // A failed test must not leave the previous (successful) server's tool
        // list on screen to select/save against.
        setDiscoveredTools(null);
      }
    } catch (error) {
      if (testReqId.current !== reqId) return;
      Sentry.captureException(error, {
        extra: { context: 'org-mcp-tool-validate' },
      });
      setDiscoveredTools(null);
      setTestResult({
        success: false,
        message:
          error instanceof Error ? error.message : 'Connection test failed',
      });
    }
  };

  const handleSave = async () => {
    if (!baseValid) {
      notification.error('Tool name and server URL are required');
      return;
    }
    if (!secretSatisfied) {
      notification.error('A secret is required for the selected auth type');
      return;
    }
    if (!selectionValid) {
      notification.error(
        'Select at least one tool to expose, or enable “Expose all tools”',
      );
      return;
    }
    setSaveError(null);
    try {
      if (isEditing && tool) {
        await update.mutateAsync({ toolId: tool.id, input: buildInput() });
        notification.success('Tool updated');
      } else {
        await create.mutateAsync(buildInput());
        notification.success('Tool added');
      }
      onOpenChange(false);
    } catch (error) {
      Sentry.captureException(error, {
        extra: { context: 'org-mcp-tool-save' },
      });
      setSaveError(
        error instanceof Error ? error.message : 'Failed to save tool',
      );
    }
  };

  const saving = create.isPending || update.isPending;

  const footer = useMemo(
    () => (
      <>
        <DialogClose asChild>
          <Button variant='outline'>Cancel</Button>
        </DialogClose>
        <Button
          variant='outline'
          onClick={handleTest}
          disabled={!baseValid || validate.isPending}
        >
          {validate.isPending ? (
            <>
              <LoadingSpinner size='sm' /> Testing...
            </>
          ) : (
            'Test Connection'
          )}
        </Button>
        <Button
          onClick={handleSave}
          disabled={!baseValid || saving || !selectionValid}
        >
          {saving ? (
            <>
              <LoadingSpinner size='sm' /> Saving...
            </>
          ) : isEditing ? (
            'Save changes'
          ) : (
            'Add tool'
          )}
        </Button>
      </>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      // tool?.id keeps handleSave's closure bound to the row currently being
      // edited — without it, switching to another tool with identical visible
      // field values would reuse a stale closure and update the wrong toolId.
      tool?.id,
      baseValid,
      secretSatisfied,
      validate.isPending,
      saving,
      selectionValid,
      isEditing,
      name,
      serverUrl,
      authType,
      authHeaderName,
      secret,
      transport,
      description,
      iconUrl,
      tags,
      selectedTools,
    ],
  );

  return (
    <Modal
      isDialogOpen={isOpen}
      setIsDialogOpen={onOpenChange}
      title={isEditing ? `Edit ${tool?.name}` : 'Add tool'}
      description='Connect a third-party MCP server as a tool for your organization.'
      footer={footer}
    >
      <div className='space-y-4'>
        <div className='space-y-2'>
          <Label htmlFor='omt-name'>Name</Label>
          <Input
            id='omt-name'
            placeholder='GitHub Tools'
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className='space-y-2'>
          <Label htmlFor='omt-description'>Description</Label>
          <Textarea
            id='omt-description'
            placeholder='What this tool does'
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className='space-y-2'>
          <Label htmlFor='omt-icon'>Icon URL (optional)</Label>
          <Input
            id='omt-icon'
            placeholder='https://…/icon.png'
            value={iconUrl}
            onChange={(e) => setIconUrl(e.target.value)}
          />
        </div>

        <div className='space-y-2'>
          <Label htmlFor='omt-url'>MCP server URL</Label>
          <Input
            id='omt-url'
            placeholder='https://mcp.example.com/mcp'
            value={serverUrl}
            onChange={(e) => setServerUrl(e.target.value)}
          />
        </div>

        <div className='space-y-2'>
          <Label>Transport</Label>
          <Select
            value={transport}
            onValueChange={(v) => setTransport(v as OrgMcpToolTransport)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TRANSPORTS.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className='space-y-2'>
          <Label>Authentication</Label>
          <Select
            value={authType}
            onValueChange={(v) => setAuthType(v as OrgMcpToolAuthType)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {AUTH_TYPES.map((a) => (
                <SelectItem key={a.value} value={a.value}>
                  {a.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {authType === 'api_key' && (
          <div className='space-y-2'>
            <Label htmlFor='omt-header'>Header name</Label>
            <Input
              id='omt-header'
              placeholder='X-API-Key'
              value={authHeaderName}
              onChange={(e) => setAuthHeaderName(e.target.value)}
            />
          </div>
        )}

        {needsSecret && (
          <div className='space-y-2'>
            <Label htmlFor='omt-secret'>
              {authType === 'custom_headers'
                ? 'Headers JSON ({"Header": "value"})'
                : 'Secret'}
            </Label>
            {authType === 'custom_headers' ? (
              <Textarea
                id='omt-secret'
                placeholder='{"Authorization": "Bearer …", "X-Org": "…"}'
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
              />
            ) : (
              <Input
                id='omt-secret'
                type='password'
                placeholder={
                  isEditing ? 'Leave blank to keep existing' : 'Secret value'
                }
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
              />
            )}
          </div>
        )}

        <div className='space-y-2'>
          <Label>Tags</Label>
          <TagInput value={tags} onChange={setTags} />
        </div>

        <div className='space-y-2'>
          <div className='flex items-center justify-between'>
            <Label>Tools</Label>
            {discoveredTools ? (
              <span className='text-xs text-muted-foreground'>
                {selectedTools === null
                  ? `All ${discoveredTools.length} exposed`
                  : `${selectedTools.length} of ${discoveredTools.length} selected`}
              </span>
            ) : null}
          </div>
          {discoveredTools ? (
            <div className='rounded-md border'>
              <label className='flex cursor-pointer items-center gap-2 border-b px-3 py-2 text-sm font-medium'>
                <Checkbox
                  checked={allToolsState}
                  onCheckedChange={(c) => handleToggleAll(c === true)}
                />
                Expose all tools
              </label>
              <div className='max-h-48 space-y-1 overflow-y-auto p-2'>
                {discoveredTools.map((t) => (
                  <label
                    key={t.name}
                    className='flex cursor-pointer items-start gap-2 rounded px-1 py-1 text-sm hover:bg-muted/50'
                  >
                    <Checkbox
                      className='mt-0.5'
                      checked={toolChecked(t.name)}
                      onCheckedChange={(c) =>
                        handleToggleTool(t.name, c === true)
                      }
                    />
                    <span className='min-w-0'>
                      <span className='block truncate font-medium'>
                        {t.name}
                      </span>
                      {t.description ? (
                        <span className='block truncate text-xs text-muted-foreground'>
                          {t.description}
                        </span>
                      ) : null}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          ) : (
            <p className='text-xs text-muted-foreground'>
              {selectedTools === null
                ? 'All tools are exposed. Test the connection to choose specific tools.'
                : `Exposing ${selectedTools.length} selected tool(s). Test the connection to change the selection.`}
            </p>
          )}
          {discoveredTools &&
          selectedTools !== null &&
          selectedTools.length === 0 ? (
            <p className='text-xs text-red-600 dark:text-red-400'>
              Select at least one tool, or enable “Expose all tools”.
            </p>
          ) : null}
        </div>

        {testResult && (
          <div
            className={`relative flex flex-col gap-1 rounded-md border p-3 pr-10 text-sm ${
              testResult.success
                ? 'border-green-200 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-950 dark:text-green-200'
                : 'border-red-200 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200'
            }`}
          >
            <div className='flex items-start gap-2'>
              {testResult.success ? (
                <CheckCircle className='size-4 shrink-0 mt-0.5' />
              ) : (
                <XCircle className='size-4 shrink-0 mt-0.5' />
              )}
              <span className='break-words'>{testResult.message}</span>
            </div>
            {testResult.success && testResult.tools?.length ? (
              <div className='ml-6 text-xs opacity-80'>
                {testResult.tools.map((t) => t.name).join(', ')}
              </div>
            ) : null}
            <button
              type='button'
              aria-label='Dismiss'
              onClick={() => setTestResult(null)}
              className='absolute top-2 right-2 inline-flex size-6 items-center justify-center rounded opacity-70 hover:opacity-100 hover:bg-black/5 dark:hover:bg-white/10 focus:outline-none'
            >
              <X className='size-4' />
            </button>
          </div>
        )}

        {saveError && (
          <div className='relative rounded-md border border-red-200 bg-red-50 p-3 pr-10 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200'>
            <p className='font-medium'>Could not save tool</p>
            <p className='break-words'>{saveError}</p>
            <button
              type='button'
              aria-label='Dismiss'
              onClick={() => setSaveError(null)}
              className='absolute top-2 right-2 inline-flex size-6 items-center justify-center rounded opacity-70 hover:opacity-100 hover:bg-black/5 dark:hover:bg-white/10 focus:outline-none'
            >
              <X className='size-4' />
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
};
